import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Card,
  CardContent,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  useTheme as useMuiTheme,
  Tooltip,
  useMediaQuery,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { 
  TimelineOutlined, 
  LocationOnOutlined, 
  InfoOutlined, 
  PrintOutlined,
  Agriculture as AgricultureIcon
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ZAxis,
  BarChart,
  Bar
} from 'recharts';

const SexRatioAnalytics = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [treeRecords, setTreeRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedView, setSelectedView] = useState('time');
  const [selectedPlantationType, setSelectedPlantationType] = useState('all');
  const [timeData, setTimeData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [clientDistributionData, setClientDistributionData] = useState([]);
  const [plantationTypeData, setPlantationTypeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'year'
  const [plantationTypes, setPlantationTypes] = useState([]);
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (treeRecords.length > 0) {
      processData();
    }
  }, [treeRecords, selectedClient, timeRange, selectedPlantationType]);

  const loadData = () => {
    setLoading(true);
    setError(null);
    try {
      // Load tree records from localStorage
      const storedRecords = localStorage.getItem('treeRecords');
      if (storedRecords) {
        const records = JSON.parse(storedRecords);
        // Sort by timestamp (oldest first for time series)
        records.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setTreeRecords(records);
      } else {
        setTreeRecords([]);
      }

      // Load clients
      const storedClients = localStorage.getItem('clients');
      if (storedClients) {
        const clients = JSON.parse(storedClients);
        setClients(clients);
        
        // Extract unique plantation types
        const types = [...new Set(clients.map(client => client.plantationType).filter(Boolean))];
        setPlantationTypes(types);
      } else {
        setClients([]);
        setPlantationTypes([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processData = () => {
    if (treeRecords.length === 0) return;

    // Filter records by client if needed
    let filteredRecords = treeRecords;
    
    if (selectedClient !== 'all') {
      filteredRecords = filteredRecords.filter(record => record.clientId === selectedClient);
    }
    
    // Filter by plantation type if needed
    if (selectedPlantationType !== 'all') {
      // Get client IDs for the selected plantation type
      const clientIdsWithType = clients
        .filter(client => client.plantationType === selectedPlantationType)
        .map(client => client.id);
      
      filteredRecords = filteredRecords.filter(record => 
        clientIdsWithType.includes(record.clientId)
      );
    }

    // Process time series data
    processTimeSeriesData(filteredRecords);
    
    // Process location-based data
    processLocationData(filteredRecords);
    
    // Process client distribution data
    processClientDistributionData();
    
    // Process plantation type data
    processPlantationTypeData();
  };

  const processTimeSeriesData = (records) => {
    if (records.length === 0) {
      setTimeData([]);
      return;
    }

    // Group by time period (day, week, month)
    const timeGroups = {};
    
    records.forEach(record => {
      const date = new Date(record.timestamp);
      let timeKey;
      
      // Format the time key based on selected range
      if (timeRange === 'week') {
        // Group by day of week
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
        const weekNumber = getWeekNumber(date);
        const year = date.getFullYear();
        timeKey = `${dayOfWeek} (W${weekNumber}, ${year})`;
      } else if (timeRange === 'month') {
        // Group by day of month
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        timeKey = `${day} ${month} ${year}`;
      } else if (timeRange === 'year') {
        // Group by month
        timeKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
      
      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = {
          date: date,
          timeKey: timeKey,
          count: 0,
          totalRatio: 0,
          minRatio: 1,
          maxRatio: 0,
          female: 0,
          male: 0
        };
      }
      
      const group = timeGroups[timeKey];
      const ratio = parseFloat(record.sexRatio);
      
      group.count++;
      group.totalRatio += ratio;
      group.female += parseInt(record.femaleFlowers);
      group.male += parseInt(record.maleFlowers);
      
      if (ratio < group.minRatio) group.minRatio = ratio;
      if (ratio > group.maxRatio) group.maxRatio = ratio;
    });
    
    // Convert to array and calculate averages
    const timeSeriesData = Object.values(timeGroups).map(group => ({
      ...group,
      averageRatio: group.totalRatio / group.count,
      calculatedRatio: group.female / (group.female + group.male)
    }));
    
    // Sort by date
    timeSeriesData.sort((a, b) => a.date - b.date);
    
    setTimeData(timeSeriesData);
  };

  const processLocationData = (records) => {
    if (records.length === 0) {
      setLocationData([]);
      return;
    }
    
    // Create location data points
    const locationPoints = records
      .filter(record => record.latitude && record.longitude) // Only include records with location data
      .map(record => ({
        x: parseFloat(record.longitude),
        y: parseFloat(record.latitude),
        sexRatio: parseFloat(record.sexRatio),
        treeId: record.treeId,
        date: new Date(record.timestamp).toLocaleDateString(),
        clientName: getClientName(record.clientId),
        plantationType: getPlantationType(record.clientId)
      }));
      
    setLocationData(locationPoints);
  };

  const processClientDistributionData = () => {
    if (clients.length === 0 || treeRecords.length === 0) {
      setClientDistributionData([]);
      return;
    }
    
    // Calculate sex ratio distribution by client
    const clientData = clients.map(client => {
      const clientRecords = treeRecords.filter(record => record.clientId === client.id);
      
      if (clientRecords.length === 0) {
        return {
          name: client.name,
          avgRatio: 0,
          count: 0,
          lowCount: 0,
          mediumCount: 0,
          highCount: 0
        };
      }
      
      let totalRatio = 0;
      let lowCount = 0;
      let mediumCount = 0; 
      let highCount = 0;
      
      clientRecords.forEach(record => {
        const ratio = parseFloat(record.sexRatio);
        totalRatio += ratio;
        
        if (ratio < 0.4) lowCount++;
        else if (ratio <= 0.6) mediumCount++;
        else highCount++;
      });
      
      return {
        name: client.name,
        avgRatio: totalRatio / clientRecords.length,
        count: clientRecords.length,
        lowCount,
        mediumCount,
        highCount
      };
    });
    
    // Filter out clients with no data
    const filteredData = clientData.filter(client => client.count > 0);
    
    // Sort by average ratio (descending)
    filteredData.sort((a, b) => b.avgRatio - a.avgRatio);
    
    setClientDistributionData(filteredData);
  };

  const processPlantationTypeData = () => {
    if (clients.length === 0 || treeRecords.length === 0) {
      setPlantationTypeData([]);
      return;
    }
    
    // Group clients by plantation type
    const typeGroups = {};
    
    // Initialize groups for each plantation type
    plantationTypes.forEach(type => {
      typeGroups[type] = {
        name: type,
        totalRatio: 0,
        count: 0,
        femaleFlowers: 0,
        maleFlowers: 0,
        lowCount: 0,
        mediumCount: 0,
        highCount: 0,
        clientIds: []
      };
    });
    
    // Add client IDs to corresponding plantation type groups
    clients.forEach(client => {
      if (client.plantationType && typeGroups[client.plantationType]) {
        typeGroups[client.plantationType].clientIds.push(client.id);
      }
    });
    
    // Process tree records for each plantation type
    treeRecords.forEach(record => {
      const client = clients.find(c => c.id === record.clientId);
      if (!client || !client.plantationType) return;
      
      const type = client.plantationType;
      if (!typeGroups[type]) return;
      
      const ratio = parseFloat(record.sexRatio);
      const female = parseInt(record.femaleFlowers);
      const male = parseInt(record.maleFlowers);
      
      typeGroups[type].totalRatio += ratio;
      typeGroups[type].count++;
      typeGroups[type].femaleFlowers += female;
      typeGroups[type].maleFlowers += male;
      
      if (ratio < 0.4) typeGroups[type].lowCount++;
      else if (ratio <= 0.6) typeGroups[type].mediumCount++;
      else typeGroups[type].highCount++;
    });
    
    // Calculate averages and format data for chart
    const plantationData = Object.values(typeGroups)
      .filter(group => group.count > 0)
      .map(group => ({
        ...group,
        avgRatio: group.totalRatio / group.count,
        calculatedRatio: group.femaleFlowers / (group.femaleFlowers + group.maleFlowers || 1),
        treeCount: group.count
      }));
    
    // Sort by average ratio
    plantationData.sort((a, b) => b.avgRatio - a.avgRatio);
    
    setPlantationTypeData(plantationData);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown';
  };

  const getPlantationType = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.plantationType || 'Unknown';
  };

  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const handleClientChange = (event) => {
    setSelectedClient(event.target.value);
  };

  const handlePlantationTypeChange = (event) => {
    setSelectedPlantationType(event.target.value);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setSelectedView(newView);
    }
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const handlePrintReport = () => {
    setOpenPrintDialog(true);
  };

  const handleClosePrintDialog = () => {
    setOpenPrintDialog(false);
  };

  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Plantation Type Analysis Report - Ranko Tracker</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.5;
              margin: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 1px solid #ddd;
              padding-bottom: 10px;
            }
            .plantation-card {
              border: 1px solid #ddd;
              border-radius: 4px;
              margin-bottom: 15px;
              padding: 15px;
              page-break-inside: avoid;
            }
            .plantation-header {
              border-bottom: 1px solid #eee;
              margin-bottom: 10px;
              padding-bottom: 10px;
              font-size: 18px;
              font-weight: bold;
            }
            .ratio-value {
              font-size: 24px;
              font-weight: bold;
              margin: 10px 0;
            }
            .good { color: #2e7d32; }
            .medium { color: #ed6c02; }
            .poor { color: #d32f2f; }
            .data-row {
              margin: 5px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
            }
            .recommendations {
              margin-top: 20px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
            }
            .date-generated {
              font-size: 12px;
              color: #666;
              margin-top: 30px;
              text-align: center;
            }
            @media print {
              body {
                margin: 1cm;
              }
              .plantation-card {
                break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Plantation Type Analysis Report</h1>
            <p>Ranko Tracker | Sex Ratio Analysis</p>
          </div>
          ${content.innerHTML}
          <div class="date-generated">
            Report generated on ${new Date().toLocaleString()} 
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Add a small delay to ensure content is fully loaded
    setTimeout(() => {
      printWindow.print();
    }, 500);
    
    setOpenPrintDialog(false);
  };

  const getRatioColor = (ratio) => {
    if (ratio === undefined || ratio === null || isNaN(ratio)) {
      return muiTheme.palette.grey[500];
    }
    
    if (ratio < 0.4) return muiTheme.palette.error.main;
    if (ratio <= 0.6) return muiTheme.palette.warning.main;
    return muiTheme.palette.success.main;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (treeRecords.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No tree records found. Start scanning trees to collect data for analysis.
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Sex Ratio Analytics
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1" paragraph>
            The sex ratio of oil palm indicates the proportion of female flowers to total flowers. A higher sex ratio leads to higher yield potential as only female flowers develop into fruit. Monitoring trends over time and across locations helps identify areas for improvement.
          </Typography>
          <Typography variant="body1" paragraph>
            Grouping analytics by plantation type helps identify which types perform better for oil palm sex ratios, allowing for optimized management strategies across different plantation environments.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlined sx={{ color: 'info.main', mr: 1 }} />
            <Typography variant="body2" color="textSecondary">
              <strong>Low ratio (&lt;0.4):</strong> Poor yield potential
              &nbsp; | &nbsp;
              <strong>Medium ratio (0.4-0.6):</strong> Moderate yield potential
              &nbsp; | &nbsp;
              <strong>High ratio (&gt;0.6):</strong> Excellent yield potential
            </Typography>
          </Box>
        </CardContent>
      </Card>
      
      <Paper 
        elevation={2} 
        sx={{ 
          p: {xs: 2, sm: 3}, 
          mb: 3,
          borderRadius: 2
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="plantation-type-select-label">Plantation Type</InputLabel>
              <Select
                labelId="plantation-type-select-label"
                id="plantation-type-select"
                value={selectedPlantationType}
                label="Plantation Type"
                onChange={handlePlantationTypeChange}
              >
                <MenuItem value="all">All Plantation Types</MenuItem>
                {plantationTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="client-select-label">Estate</InputLabel>
              <Select
                labelId="client-select-label"
                id="client-select"
                value={selectedClient}
                label="Estate"
                onChange={handleClientChange}
              >
                <MenuItem value="all">All Estates</MenuItem>
                {clients
                  .filter(client => selectedPlantationType === 'all' || client.plantationType === selectedPlantationType)
                  .map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={12} md={6}>
            <ToggleButtonGroup
              value={selectedView}
              exclusive
              onChange={handleViewChange}
              aria-label="data view"
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                width: {xs: '100%', sm: 'auto'},
                '& .MuiToggleButtonGroup-grouped': {
                  border: 1,
                  px: {xs: 1, sm: 2},
                  textTransform: 'none'
                }
              }}
            >
              <ToggleButton value="time" aria-label="time view">
                <TimelineOutlined sx={{ mr: {xs: 0.5, sm: 1} }} />
                <Typography 
                  component="span" 
                  sx={{ 
                    display: {xs: 'none', sm: 'inline'},
                    fontSize: {sm: '0.875rem', md: 'inherit'}
                  }}
                >
                  Time
                </Typography>
              </ToggleButton>
              <ToggleButton value="location" aria-label="location view">
                <LocationOnOutlined sx={{ mr: {xs: 0.5, sm: 1} }} />
                <Typography 
                  component="span" 
                  sx={{ 
                    display: {xs: 'none', sm: 'inline'},
                    fontSize: {sm: '0.875rem', md: 'inherit'}
                  }}
                >
                  Location
                </Typography>
              </ToggleButton>
              <ToggleButton value="distribution" aria-label="distribution view">
                <InfoOutlined sx={{ mr: {xs: 0.5, sm: 1} }} />
                <Typography 
                  component="span" 
                  sx={{ 
                    display: {xs: 'none', sm: 'inline'},
                    fontSize: {sm: '0.875rem', md: 'inherit'}
                  }}
                >
                  Distribution
                </Typography>
              </ToggleButton>
              <ToggleButton value="plantation" aria-label="plantation view">
                <AgricultureIcon sx={{ mr: {xs: 0.5, sm: 1} }} />
                <Typography 
                  component="span" 
                  sx={{ 
                    display: {xs: 'none', sm: 'inline'},
                    fontSize: {sm: '0.875rem', md: 'inherit'}
                  }}
                >
                  Plantation
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          
          {selectedView === 'time' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="time-range-label">Time Range</InputLabel>
                <Select
                  labelId="time-range-label"
                  id="time-range-select"
                  value={timeRange}
                  label="Time Range"
                  onChange={handleTimeRangeChange}
                >
                  <MenuItem value="week">Weekly Analysis</MenuItem>
                  <MenuItem value="month">Monthly Analysis</MenuItem>
                  <MenuItem value="year">Yearly Analysis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          
          {selectedView === 'plantation' && (
            <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<PrintOutlined />}
                onClick={handlePrintReport}
                size={isMobile ? 'small' : 'medium'}
              >
                Print Report
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {selectedView === 'time' && (
        <Card sx={{ mb: 3, overflow: 'visible' }}>
          <CardContent sx={{ 
            p: { xs: 2, sm: 3 },
            overflowX: 'auto'
          }}>
            <Typography variant="h6" gutterBottom>
              <TimelineOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
              Sex Ratio Trends Over Time
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {timeData.length > 0 ? (
              <Box sx={{ 
                height: { xs: 350, sm: 400 }, 
                minWidth: { xs: 300, sm: 'auto' }
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={timeData} 
                    margin={{ 
                      top: 5, 
                      right: 20, 
                      left: isMobile ? 0 : 20, 
                      bottom: isMobile ? 120 : 100 
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="timeKey" 
                      angle={-45} 
                      textAnchor="end" 
                      height={70}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                      interval={isMobile ? 1 : 0}
                    />
                    <YAxis 
                      domain={[0, 1]} 
                      ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]} 
                      label={{ 
                        value: isMobile ? 'Sex Ratio' : 'Sex Ratio (Female/Total)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { 
                          textAnchor: 'middle',
                          fontSize: isMobile ? 10 : 12
                        }
                      }}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => [
                        value.toFixed(3), 
                        name === 'averageRatio' ? 'Average Sex Ratio' : 'Calculated Sex Ratio'
                      ]}
                      wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="averageRatio" 
                      name="Average Sex Ratio" 
                      stroke={muiTheme.palette.primary.main} 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 8 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="calculatedRatio" 
                      name="Calculated Ratio (Total Female/Total Flowers)" 
                      stroke={muiTheme.palette.secondary.main} 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="info">No time series data available for the selected filters.</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {selectedView === 'location' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ 
            p: { xs: 2, sm: 3 },
            overflowX: 'auto'
          }}>
            <Typography variant="h6" gutterBottom>
              <LocationOnOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
              Sex Ratio by Location
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {locationData.length > 0 ? (
              <Box sx={{ 
                height: { xs: 400, sm: 500 },
                minWidth: { xs: 300, sm: 'auto' }
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ 
                      top: 20, 
                      right: 20, 
                      bottom: isMobile ? 30 : 20, 
                      left: isMobile ? 0 : 20 
                    }}
                  >
                    <CartesianGrid />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name="Longitude" 
                      label={{ 
                        value: 'Longitude', 
                        position: 'bottom', 
                        offset: 0,
                        fontSize: isMobile ? 10 : 12
                      }}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name="Latitude" 
                      label={{ 
                        value: 'Latitude', 
                        angle: -90, 
                        position: 'insideLeft',
                        fontSize: isMobile ? 10 : 12
                      }}
                      tick={{ fontSize: isMobile ? 10 : 12 }}
                    />
                    <ZAxis 
                      type="number" 
                      range={[50, 500]} 
                      dataKey="sexRatio" 
                    />
                    <RechartsTooltip 
                      cursor={{ strokeDasharray: '3 3' }}
                      wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      formatter={(value, name, props) => {
                        const payload = props.payload[0];
                        return [
                          <div key="tooltip">
                            <div><strong>Tree ID:</strong> {payload?.treeId}</div>
                            <div><strong>Sex Ratio:</strong> {payload?.sexRatio?.toFixed(3)}</div>
                            <div><strong>Date:</strong> {payload?.date}</div>
                            <div><strong>Estate:</strong> {payload?.clientName}</div>
                            <div><strong>Plantation Type:</strong> {payload?.plantationType}</div>
                            <div><strong>Location:</strong> [{payload?.x?.toFixed(5)}, {payload?.y?.toFixed(5)}]</div>
                          </div>,
                          ''
                        ];
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                    />
                    <Scatter 
                      name="Sex Ratio" 
                      data={locationData} 
                      fill={(entry) => getRatioColor(entry.sexRatio)}
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="info">No location data available for the selected filters. Ensure trees are scanned with location information.</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {selectedView === 'plantation' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ 
            p: { xs: 2, sm: 3 },
            overflowX: 'auto'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <AgricultureIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Sex Ratio by Plantation Type
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Understanding Plantation Type Analysis</strong>
              </Typography>
              <Typography variant="body2">
                This report displays sex ratio data grouped by plantation type, helping you identify which types of plantations are performing best. Compare the performance of different plantation types to optimize your agricultural strategy.
              </Typography>
            </Alert>
            
            {plantationTypeData.length > 0 ? (
              <>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {plantationTypeData.map(typeData => (
                    <Grid item xs={12} sm={6} md={4} key={typeData.name}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 2, 
                          borderLeft: `4px solid ${getRatioColor(typeData.avgRatio)}`,
                          height: '100%'
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          {typeData.name}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Average Sex Ratio
                          </Typography>
                          <Typography variant="h4" sx={{ color: getRatioColor(typeData.avgRatio) }}>
                            {typeData.avgRatio.toFixed(3)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Total Trees:</strong> {typeData.treeCount}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Performance:</strong> {' '}
                            <Box component="span" sx={{ color: 'error.main' }}>Low: {typeData.lowCount}</Box>, {' '}
                            <Box component="span" sx={{ color: 'warning.main' }}>Medium: {typeData.mediumCount}</Box>, {' '}
                            <Box component="span" sx={{ color: 'success.main' }}>High: {typeData.highCount}</Box>
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Female/Male:</strong> {typeData.femaleFlowers}/{typeData.maleFlowers}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            mt: 1, 
                            fontStyle: 'italic', 
                            color: typeData.avgRatio < 0.4 ? 'error.main' : (typeData.avgRatio <= 0.6 ? 'warning.main' : 'success.main')
                          }}>
                            {typeData.avgRatio < 0.4 
                              ? 'Needs attention - Low yield potential'
                              : (typeData.avgRatio <= 0.6 
                                ? 'Moderate performance - Good yield potential'
                                : 'Excellent performance - High yield potential')}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                  Plantation Type Comparison
                </Typography>
                
                <Box 
                  sx={{ 
                    height: { xs: 350, sm: 400 }, 
                    minWidth: { xs: 300, sm: 'auto' },
                    mb: 4
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={plantationTypeData}
                      layout="vertical"
                      margin={{ 
                        top: 5, 
                        right: 30, 
                        left: isMobile ? 80 : 150, 
                        bottom: 5 
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number" 
                        domain={[0, 1]} 
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        label={{ value: 'Sex Ratio (0-1)', position: 'bottom', fontSize: isMobile ? 10 : 12 }}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={isMobile ? 70 : 140}
                        tick={{ 
                          fontSize: isMobile ? 9 : 12,
                          width: isMobile ? 65 : 135,
                          textOverflow: 'ellipsis'
                        }}
                      />
                      <RechartsTooltip 
                        formatter={(value, name, props) => {
                          const payload = props.payload;
                          if (name === 'avgRatio') {
                            return [`${value.toFixed(3)} (${payload.treeCount} trees)`, 'Average Sex Ratio'];
                          }
                          if (name === 'calculatedRatio') {
                            return [`${value.toFixed(3)} (♀${payload.femaleFlowers}/♂${payload.maleFlowers})`, 'Calculated Ratio'];
                          }
                          return [value, name];
                        }}
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Bar 
                        dataKey="avgRatio" 
                        name="Average Sex Ratio" 
                        fill={muiTheme.palette.primary.main} 
                        background={{ fill: '#eee' }}
                      />
                      <Bar 
                        dataKey="calculatedRatio" 
                        name="Calculated Ratio (Total Female/Total Flowers)" 
                        fill={muiTheme.palette.secondary.main} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                  Distribution of Trees by Plantation Type
                </Typography>
                
                <Box 
                  sx={{ 
                    height: { xs: 350, sm: 400 }, 
                    minWidth: { xs: 300, sm: 'auto' }
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={plantationTypeData}
                      margin={{ 
                        top: 5, 
                        right: 30, 
                        left: isMobile ? 80 : 150, 
                        bottom: 5 
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name"
                        tick={{ fontSize: isMobile ? 10 : 12, angle: -45, textAnchor: 'end' }}
                        height={80}
                      />
                      <YAxis 
                        label={{ 
                          value: 'Number of Trees', 
                          angle: -90, 
                          position: 'insideLeft',
                          fontSize: isMobile ? 10 : 12
                        }}
                      />
                      <RechartsTooltip 
                        formatter={(value, name) => {
                          const labels = {
                            lowCount: 'Low Ratio Trees (<0.4)',
                            mediumCount: 'Medium Ratio Trees (0.4-0.6)',
                            highCount: 'High Ratio Trees (>0.6)'
                          };
                          return [value, labels[name] || name];
                        }}
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Bar 
                        dataKey="lowCount" 
                        name="Low Ratio Trees" 
                        stackId="a"
                        fill={muiTheme.palette.error.main} 
                      />
                      <Bar 
                        dataKey="mediumCount" 
                        name="Medium Ratio Trees" 
                        stackId="a"
                        fill={muiTheme.palette.warning.main} 
                      />
                      <Bar 
                        dataKey="highCount" 
                        name="High Ratio Trees" 
                        stackId="a"
                        fill={muiTheme.palette.success.main} 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Insights & Recommendations
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    <strong>Best Performing Plantation Type: </strong> 
                    {plantationTypeData.length > 0 ? plantationTypeData[0].name : 'N/A'} with an average sex ratio of {plantationTypeData.length > 0 ? plantationTypeData[0].avgRatio.toFixed(3) : 'N/A'}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Key Observations:</strong>
                  </Typography>
                  <ul>
                    {plantationTypeData.map((type, index) => (
                      <li key={index}>
                        <Typography variant="body2">
                          {type.name}: {type.avgRatio < 0.4 
                            ? 'Needs improvement. Consider reviewing fertilization and management practices.' 
                            : (type.avgRatio <= 0.6 
                              ? 'Performing moderately well. Continue current management practices with minor adjustments.'
                              : 'Excellent performance. Document current practices to replicate in other plantations.')}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              </>
            ) : (
              <Alert severity="info">No plantation type data available. Add more estates with plantation types and scan trees to see comparative analysis.</Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ 
          p: { xs: 2, sm: 3 },
          overflowX: 'auto'
        }}>
          <Typography variant="h6" gutterBottom>
            Sex Ratio Distribution by Estate
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {clientDistributionData.length > 0 ? (
            <Box 
              sx={{ 
                height: { xs: 350, sm: 400 }, 
                minWidth: { xs: 300, sm: 'auto' }
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={clientDistributionData}
                  layout="vertical"
                  margin={{ 
                    top: 5, 
                    right: 30, 
                    left: isMobile ? 80 : 150, 
                    bottom: 5 
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number" 
                    domain={[0, 1]} 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={isMobile ? 70 : 140}
                    tick={{ 
                      fontSize: isMobile ? 9 : 12,
                      width: isMobile ? 65 : 135,
                      textOverflow: 'ellipsis'
                    }}
                    tickFormatter={(value) => {
                      if (isMobile && value.length > 12) {
                        return value.substring(0, 12) + '...';
                      }
                      return value;
                    }}
                  />
                  <RechartsTooltip 
                    formatter={(value, name, props) => {
                      if (name === 'avgRatio') {
                        return [`${value.toFixed(3)} (${props.payload.count} trees)`, 'Average Sex Ratio'];
                      }
                      return [value, name];
                    }}
                    wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                  />
                  <Bar 
                    dataKey="avgRatio" 
                    name="Average Sex Ratio" 
                    fill={muiTheme.palette.primary.main} 
                    background={{ fill: '#eee' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Alert severity="info">No distribution data available. Add more estates and scan trees to see comparative analysis.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Print Dialog */}
      <Dialog
        open={openPrintDialog}
        onClose={handleClosePrintDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Plantation Type Analysis Report
        </DialogTitle>
        <DialogContent dividers>
          <div ref={printRef}>
            <Typography variant="h4" align="center" gutterBottom>
              Plantation Type Analysis Report
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
              Sex Ratio Analysis by Plantation Type
            </Typography>
            
            <Box sx={{ my: 3 }}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body2" paragraph>
                This report provides an analysis of sex ratio data across different plantation types. 
                The sex ratio (female flowers to total flowers) is a key indicator of yield potential in oil palm plantations.
              </Typography>
              
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                  <tr>
                    <th>Plantation Type</th>
                    <th>Avg. Sex Ratio</th>
                    <th>Trees</th>
                    <th>Female/Male</th>
                    <th>Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {plantationTypeData.map((type, index) => (
                    <tr key={index}>
                      <td><strong>{type.name}</strong></td>
                      <td 
                        style={{ 
                          color: type.avgRatio < 0.4 ? '#d32f2f' : (type.avgRatio <= 0.6 ? '#ed6c02' : '#2e7d32'),
                          fontWeight: 'bold'
                        }}
                      >
                        {type.avgRatio.toFixed(3)}
                      </td>
                      <td>{type.treeCount}</td>
                      <td>{type.femaleFlowers}/{type.maleFlowers}</td>
                      <td>
                        Low: {type.lowCount}, Med: {type.mediumCount}, High: {type.highCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            
            <Box sx={{ my: 3 }}>
              <Typography variant="h6" gutterBottom>
                Detailed Analysis by Plantation Type
              </Typography>
              
              {plantationTypeData.map((type, index) => (
                <div 
                  key={index} 
                  style={{ 
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '15px',
                    marginBottom: '15px',
                    borderLeft: `5px solid ${
                      type.avgRatio < 0.4 ? '#d32f2f' : (type.avgRatio <= 0.6 ? '#ed6c02' : '#2e7d32')
                    }`
                  }}
                >
                  <div style={{ 
                    borderBottom: '1px solid #eee',
                    marginBottom: '10px',
                    paddingBottom: '10px',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    {type.name} Plantation
                  </div>
                  
                  <div style={{ 
                    color: type.avgRatio < 0.4 ? '#d32f2f' : (type.avgRatio <= 0.6 ? '#ed6c02' : '#2e7d32'),
                    fontSize: '24px',
                    fontWeight: 'bold',
                    margin: '10px 0'
                  }}>
                    Sex Ratio: {type.avgRatio.toFixed(3)}
                  </div>
                  
                  <div style={{ margin: '5px 0' }}>
                    <strong>Total Trees:</strong> {type.treeCount}
                  </div>
                  <div style={{ margin: '5px 0' }}>
                    <strong>Female Flowers:</strong> {type.femaleFlowers}
                  </div>
                  <div style={{ margin: '5px 0' }}>
                    <strong>Male Flowers:</strong> {type.maleFlowers}
                  </div>
                  <div style={{ margin: '5px 0' }}>
                    <strong>Distribution:</strong> Low: {type.lowCount} | Medium: {type.mediumCount} | High: {type.highCount}
                  </div>
                  
                  <div style={{ 
                    marginTop: '20px',
                    paddingTop: '10px',
                    borderTop: '1px solid #ddd'
                  }}>
                    <strong>Recommendation:</strong> {type.avgRatio < 0.4 
                      ? 'Needs improvement. Consider reviewing fertilization and management practices.' 
                      : (type.avgRatio <= 0.6 
                        ? 'Performing moderately well. Continue current management practices with minor adjustments.'
                        : 'Excellent performance. Document current practices to replicate in other plantations.')}
                  </div>
                </div>
              ))}
            </Box>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Key Findings & Recommendations
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Best Performing Type:</strong> {plantationTypeData.length > 0 ? plantationTypeData[0].name : 'N/A'} 
                with an average sex ratio of {plantationTypeData.length > 0 ? plantationTypeData[0].avgRatio.toFixed(3) : 'N/A'}
              </Typography>
              
              <Typography variant="body2" paragraph>
                <strong>Actions to Improve Performance:</strong>
              </Typography>
              
              <ol>
                <li>
                  <Typography variant="body2">
                    Monitor and compare management practices between plantation types to identify factors contributing to higher sex ratios.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    For low-performing plantation types, review soil conditions, irrigation patterns, and fertilization schedules.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Consider environmental factors specific to each plantation type that may affect flower development.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Document best practices from high-performing plantation types to implement across similar plantations.
                  </Typography>
                </li>
              </ol>
            </Box>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog}>Cancel</Button>
          <Button 
            onClick={handlePrint} 
            variant="contained" 
            color="primary" 
            startIcon={<PrintOutlined />}
          >
            Print Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SexRatioAnalytics; 